import React, { useState, useMemo } from 'react';
import { Users, UserPlus, Info, Calculator } from 'lucide-react';
import { Card } from '../ui';
import { Input } from '../ui';

export function PanhellenicQuotaCalculator() {
  const [totalPnms, setTotalPnms] = useState<number>(450);
  const [numChapters, setNumChapters] = useState<number>(8);
  const [quotaAdditionPercentage, setQuotaAdditionPercentage] = useState<number>(5);

  const model = useMemo(() => {
    // Primary Quota is strictly PNMs divided by chapters
    const rawQuota = numChapters > 0 ? totalPnms / numChapters : 0;
    const primaryQuota = Math.floor(rawQuota);
    const remainder = totalPnms % numChapters;

    // Quota additions: normally computed during bid matching for PNMs who maximized their options.
    // For modeling, we estimate a standard 5% addition rate across the system.
    const expectedAdditionsTotal = Math.round(totalPnms * (quotaAdditionPercentage / 100));
    const avgAdditionsPerChapter = numChapters > 0 ? Math.ceil(expectedAdditionsTotal / numChapters) : 0;
    
    const finalNewMemberClassSize = primaryQuota + avgAdditionsPerChapter;

    return {
      primaryQuota,
      remainder,
      avgAdditionsPerChapter,
      finalNewMemberClassSize,
      expectedAdditionsTotal
    };
  }, [totalPnms, numChapters, quotaAdditionPercentage]);

  return (
    <div className="max-w-5xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-700 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Calculator className="w-6 h-6 mr-3 text-violet-200" />
            Panhellenic Quota & Bid Match Calculator
          </h2>
          <p className="text-violet-100 max-w-xl">
            Forecast your recruitment numbers before Bid Day. Model Primary Quota and estimate Quota Additions to properly prepare your new member educators.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        
        {/* Controls */}
        <Card className="p-6 space-y-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <Users className="w-4 h-4 mr-2 text-violet-500" /> PNMs Attending Preference Round
              </label>
              <Input 
                type="number" 
                value={totalPnms} 
                onChange={(e) => setTotalPnms(Number(e.target.value))}
                className="text-lg font-bold"
              />
              <p className="text-xs text-slate-400 mt-1">Number of women who signed the MRABA.</p>
            </div>
            
            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase flex items-center mb-2">
                <HomeIcon className="w-4 h-4 mr-2 text-violet-500" /> Number of Chapters
              </label>
              <Input 
                type="number" 
                value={numChapters} 
                onChange={(e) => setNumChapters(Number(e.target.value))}
              />
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
              <label className="text-sm font-semibold text-slate-500 uppercase flex justify-between mb-2">
                <span>Estimated Quota Addition Rate</span>
                <span className="font-bold text-slate-900 dark:text-white">{quotaAdditionPercentage}%</span>
              </label>
              <input 
                type="range" 
                min="0" 
                max="15" 
                value={quotaAdditionPercentage} 
                onChange={(e) => setQuotaAdditionPercentage(Number(e.target.value))}
                className="w-full accent-violet-600"
              />
              <p className="text-xs text-slate-400 mt-2">
                Percentage of PNMs who maximize options but miss primary matching. Usually 2-5% nationally.
              </p>
            </div>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-violet-900 text-white flex flex-col justify-center min-h-[200px]">
            <h3 className="text-sm font-bold text-violet-300 uppercase tracking-wider mb-2">Primary Quota</h3>
            
            <div className="flex items-end gap-4">
              <div className="text-7xl font-black text-white tracking-tight leading-none">
                {model.primaryQuota}
              </div>
              {model.remainder > 0 && (
                <div className="text-violet-300 font-semibold mb-2">
                  + {model.remainder} remainder PNMs
                </div>
              )}
            </div>
          </Card>

          <Card className="p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800">
              <h3 className="font-bold flex items-center">
                <UserPlus className="w-4 h-4 mr-2 text-violet-500" /> Quota Additions & Final Class
              </h3>
            </div>
            
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              <div className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Estimated Quota Additions</div>
                  <div className="text-sm text-slate-500">Cross-matched PNMs placed manually</div>
                </div>
                <div className="text-2xl font-bold text-fuchsia-600 dark:text-fuchsia-400">
                  +{model.avgAdditionsPerChapter} <span className="text-sm font-normal text-slate-400">/ chapter</span>
                </div>
              </div>
              
              <div className="p-6 flex items-center justify-between bg-violet-50 dark:bg-violet-900/10">
                <div>
                  <div className="font-bold text-violet-900 dark:text-violet-100">Projected New Member Class Size</div>
                  <div className="text-sm text-violet-700 dark:text-violet-300">Target size to plan for Bid Day</div>
                </div>
                <div className="text-4xl font-black text-violet-700 dark:text-violet-400">
                  {model.finalNewMemberClassSize}
                </div>
              </div>
            </div>
          </Card>
          
          <div className="flex gap-3 text-sm text-slate-500 dark:text-slate-400 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
            <Info className="w-5 h-5 text-blue-500 shrink-0" />
            <p>
              <strong>Note on Quota Additions:</strong> Additions are placed by the RFM Specialist to ensure women who maximize their options receive a bid. The exact number of additions per chapter varies based on chapter size and historical return rates.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
}

function HomeIcon(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  )
}
